import type { IteroOrder } from '../../../../../services/iteroBrowse';
import { InlineError } from '../BrowserPrimitives';
import { CARD_CLASS, SECTION_HEADING_CLASS } from '../browserStyles';
import { PATIENT_ORDERS_PAGE_SIZE } from '../orderQuery';
import { scanCountLabel } from './profileFormat';
import { ScanTimeline } from './ScanTimeline';
import type { PatientScanHistory } from './usePatientScanHistory';

const NOTE_CLASS = 'text-[13px] text-gray-500';
const SKELETON_CARD_COUNT = 3;

function TimelineSkeleton() {
    return (
        <div className="flex flex-col gap-2.5 pl-6" aria-hidden="true">
            {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
                <div key={index} className={`${CARD_CLASS} flex flex-col gap-2 px-3.5 py-3`}>
                    <div className="h-3.5 w-2/5 animate-pulse rounded bg-gray-200" />
                    <div className="h-3 w-3/5 animate-pulse rounded bg-gray-100" />
                </div>
            ))}
        </div>
    );
}

interface HistoryBodyProps {
    hasChart: boolean;
    history: PatientScanHistory;
    returnFocusRxId: string | null;
    onOpenOrder: (order: IteroOrder) => void;
}

function HistoryBody({ hasChart, history, returnFocusRxId, onOpenOrder }: HistoryBodyProps) {
    if (!hasChart) {
        return (
            <p className={NOTE_CLASS}>
                This patient has no chart number in iTero, so their scans can&apos;t be looked up
                here. Search the Orders tab by date instead.
            </p>
        );
    }
    if (history.isPending) {
        return (
            <div aria-busy="true">
                <span className="sr-only">Loading scan history</span>
                <TimelineSkeleton />
            </div>
        );
    }
    if (history.errorMessage) {
        return <InlineError message={history.errorMessage} onRetry={history.retry} />;
    }
    if (history.orders.length === 0) {
        return <p className={NOTE_CLASS}>No scans for this patient yet.</p>;
    }
    return (
        <div className="flex flex-col gap-3">
            <ScanTimeline
                orders={history.orders}
                returnFocusRxId={returnFocusRxId}
                onOpenOrder={onOpenOrder}
            />
            {history.hasMore && (
                <p className="text-[12px] text-gray-500">
                    {`Showing the first ${PATIENT_ORDERS_PAGE_SIZE} orders. Use the Orders tab to see the rest.`}
                </p>
            )}
        </div>
    );
}

function historyCount(hasChart: boolean, history: PatientScanHistory): string | null {
    if (!hasChart || history.isPending || history.errorMessage) return null;
    return scanCountLabel(history.orders.length, history.hasMore);
}

export function ScanHistory(props: HistoryBodyProps) {
    const count = historyCount(props.hasChart, props.history);
    return (
        <section className="flex flex-col gap-3" aria-label="Scan history">
            <h4 className={SECTION_HEADING_CLASS}>
                Scan history
                {count !== null && <span className="tabular-nums"> · {count}</span>}
            </h4>
            <HistoryBody {...props} />
        </section>
    );
}
